/**
 * A template's outline as the designer edits it (Estimate Tool 8.10; Phase E14).
 *
 * Pure: an array of tasks in, an array of tasks out. The designer runs these in the browser to
 * move, add and remove lines, and the server runs the same functions before it stores a draft, so
 * the two cannot disagree about what "move this group up" or "a repeated name" means.
 *
 * An outline is a **flat list in display order**. Parentage is `parent_order`, the parent's
 * ordinal, and a task's subtree is the contiguous block that follows it up to the next task at
 * its own depth or shallower — which is what makes moving a group a matter of cutting and pasting
 * a block rather than walking a tree. `normalizeOutline` is what keeps that property true after
 * any edit: it renumbers contiguously from 1, re-points every parent reference, and recomputes
 * `task_level`.
 *
 * Renumbering is safe here in a way it is not on a plan. A plan's `display_order` is the order
 * E11 writes in, and a resumed write that found the numbers moved would point at a different task
 * than the one it wrote (8.7) — so a plan flags a removed line and never renumbers. A draft has
 * never been written from and never will be; the version that is published is frozen from then
 * on. So a draft renumbers, and removing a task here removes it for real.
 *
 * Nothing in this file can name a person. An assignment is `{ service_code, hours }` and the
 * shape is checked, not assumed.
 */
import { normalizeTaskName } from './schedule.js';

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
const round2 = (n) => Math.round(n * 100) / 100;

/**
 * Renumber an outline contiguously and recompute parentage and depth.
 *
 * The input's `display_order` values need only be ordered, not contiguous, and may reference
 * parents by their **old** ordinals — which is what an edit produces. A parent that does not
 * exist, or that comes after its child, is dropped to the root rather than left dangling: a
 * dangling reference is how a task disappears from every total without anyone noticing.
 *
 * @param {object[]} tasks
 * @returns {object[]}
 */
export function normalizeOutline(tasks) {
  const ordered = [...tasks].sort((a, b) => num(a.display_order) - num(b.display_order));
  const newOrderOfOld = new Map();
  ordered.forEach((t, i) => newOrderOfOld.set(num(t.display_order), i + 1));

  const out = ordered.map((t, i) => {
    const oldParent =
      t.parent_order === null || t.parent_order === undefined ? null : num(t.parent_order);
    let parent = oldParent === null ? null : (newOrderOfOld.get(oldParent) ?? null);
    // A parent must precede its child and be a group; anything else is a root task.
    if (parent !== null && parent >= i + 1) parent = null;
    // Predecessors are re-pointed the same way, and one that no longer exists, or that is the
    // task itself, is dropped: a dependency on nothing is how a task silently waits for ever.
    const predecessors = [
      ...new Set(
        (t.predecessor_orders ?? [])
          .map((o) => newOrderOfOld.get(num(o)) ?? null)
          .filter((o) => o !== null && o !== i + 1),
      ),
    ].sort((a, b) => a - b);
    return {
      ...t,
      display_order: i + 1,
      parent_order: parent,
      predecessor_orders: predecessors,
      is_group: Boolean(t.is_group),
      assignments: (t.assignments ?? []).map((a) => ({
        ...a,
        service_code: String(a.service_code ?? '').trim(),
        hours: round2(Math.max(0, num(a.hours))),
      })),
    };
  });
  const byOrder = new Map(out.map((t) => [t.display_order, t]));
  for (const t of out) {
    if (t.parent_order !== null && !byOrder.get(t.parent_order)?.is_group) t.parent_order = null;
  }
  for (const t of out) {
    let depth = 0;
    let p = t.parent_order;
    for (let guard = 0; guard < 50 && p !== null; guard++) {
      depth += 1;
      p = byOrder.get(p)?.parent_order ?? null;
    }
    t.task_level = depth;
    t.hours = round2(t.assignments.reduce((s, a) => s + a.hours, 0));
  }
  return out;
}

/** The index range `[from, to)` of a task's subtree in a normalized outline. */
export function subtreeRange(tasks, index) {
  const level = tasks[index].task_level;
  let to = index + 1;
  while (to < tasks.length && tasks[to].task_level > level) to += 1;
  return [index, to];
}

const indexOf = (tasks, order) => tasks.findIndex((t) => t.display_order === order);

/** The descendants of a task, in order. */
export function descendantsOf(tasks, order) {
  const i = indexOf(tasks, order);
  if (i === -1) return [];
  const [from, to] = subtreeRange(tasks, i);
  return tasks.slice(from + 1, to);
}

/**
 * A new task or group. `after` is the ordinal to insert after (its whole subtree is skipped, so
 * a task added after a group lands after the group's children); null inserts at the top.
 * `parent` is the group it belongs to, or null for the root.
 *
 * @param {object[]} tasks  normalized
 * @param {{ after?: number|null, parent?: number|null, is_group?: boolean, name?: string }} where
 */
export function addTask(tasks, { after = null, parent = null, is_group = false, name = '' } = {}) {
  const fresh = {
    display_order: 0,
    parent_order: parent,
    is_group,
    name: name || (is_group ? 'New group' : 'New task'),
    description: null,
    track_budget: !is_group,
    ends_before_delivery: null,
    duration_weekdays: is_group ? null : 1,
    predecessor_orders: [],
    assignments: [],
  };
  let at = 0;
  if (after !== null) {
    const i = indexOf(tasks, after);
    at = i === -1 ? tasks.length : subtreeRange(tasks, i)[1];
  } else if (parent !== null) {
    const i = indexOf(tasks, parent);
    at = i === -1 ? tasks.length : i + 1;
  }
  // Timing defaults to the neighbour's, so a new line lands where the reader is looking rather
  // than on the delivery date.
  const neighbour = tasks[at - 1] ?? tasks[at] ?? null;
  if (!is_group && neighbour && neighbour.ends_before_delivery !== null) {
    fresh.ends_before_delivery = neighbour.ends_before_delivery;
  }
  // A fractional order keeps the old parent references resolvable until renumbering.
  fresh.display_order = (tasks[at - 1]?.display_order ?? 0) + 0.5;
  return normalizeOutline([...tasks.slice(0, at), fresh, ...tasks.slice(at)]);
}

/** Remove a task and, for a group, everything beneath it. For real — this is a draft. */
export function removeTask(tasks, order) {
  const i = indexOf(tasks, order);
  if (i === -1) return tasks;
  const [from, to] = subtreeRange(tasks, i);
  return normalizeOutline([...tasks.slice(0, from), ...tasks.slice(to)]);
}

/**
 * Move a task (with its subtree) up or down among its siblings. A task with no sibling in that
 * direction stays where it is.
 */
export function moveTask(tasks, order, direction) {
  const i = indexOf(tasks, order);
  if (i === -1) return tasks;
  const me = tasks[i];
  const [from, to] = subtreeRange(tasks, i);
  const block = tasks.slice(from, to);
  const rest = [...tasks.slice(0, from), ...tasks.slice(to)];
  const siblings = rest.filter((t) => t.parent_order === me.parent_order);
  // Where the block sits among its siblings now, by how many siblings precede it.
  const before = siblings.filter((t) => t.display_order < me.display_order);
  let target;
  if (direction === 'up') {
    if (!before.length) return tasks;
    target = before[before.length - 1];
    const ti = rest.indexOf(target);
    return normalizeOutline(reflow([...rest.slice(0, ti), ...block, ...rest.slice(ti)]));
  }
  const afterSib = siblings.find((t) => t.display_order > me.display_order);
  if (!afterSib) return tasks;
  const ai = rest.indexOf(afterSib);
  const [, aEnd] = subtreeRange(rest, ai);
  return normalizeOutline(reflow([...rest.slice(0, aEnd), ...block, ...rest.slice(aEnd)]));
}

/**
 * Move a task (with its subtree) into another group, at the end of that group's children — or to
 * the end of the root with `parent` null. A group cannot be moved into itself or its own
 * descendant.
 */
export function reparentTask(tasks, order, parent) {
  const i = indexOf(tasks, order);
  if (i === -1) return tasks;
  const [from, to] = subtreeRange(tasks, i);
  const block = tasks.slice(from, to);
  if (parent !== null && block.some((t) => t.display_order === parent)) return tasks;
  const rest = [...tasks.slice(0, from), ...tasks.slice(to)];
  let at = rest.length;
  if (parent !== null) {
    const pi = indexOf(rest, parent);
    if (pi === -1 || !rest[pi].is_group) return tasks;
    at = subtreeRange(rest, pi)[1];
  }
  const moved = block.map((t, k) => (k === 0 ? { ...t, parent_order: parent } : t));
  return normalizeOutline(reflow([...rest.slice(0, at), ...moved, ...rest.slice(at)]));
}

/**
 * Give a re-ordered list fresh fractional orders that keep its old parent references
 * resolvable: every task keeps its old ordinal as the key its children point at, and its position
 * in the list decides where it lands after renumbering.
 */
function reflow(list) {
  const oldOf = new Map(list.map((t, i) => [t.display_order, i + 1]));
  return list.map((t, i) => ({
    ...t,
    display_order: i + 1,
    parent_order: t.parent_order === null ? null : (oldOf.get(t.parent_order) ?? null),
    predecessor_orders: (t.predecessor_orders ?? [])
      .map((o) => oldOf.get(num(o)) ?? null)
      .filter((o) => o !== null),
  }));
}

/**
 * The rules a publish checks (Estimate Tool 8.10).
 *
 * Hard findings refuse a publish; soft ones are shown and do not. Each names the line, because a
 * refusal that says "invalid" to someone looking at seventy-two lines is a refusal to help.
 *
 * @param {object[]} tasks  normalized
 * @param {{ activeServices?: Set<string>|null, history?: Map<string, object>|null,
 *           deadTaskRefMax?: number, referenceProjects?: number,
 *           rungMedianHours?: number|null }} [context]
 * @returns {{ hard: object[], soft: object[] }}
 */
export function validateOutline(tasks, context = {}) {
  const hard = [];
  const soft = [];
  const line = (t) => ({ display_order: t.display_order, name: t.name });

  if (!tasks.length) hard.push({ rule: 'empty', message: 'the outline is empty' });

  // A repeated name is a second task in Workamajig, and E11 cannot tell the two apart (8.8).
  const seen = new Map();
  for (const t of tasks) {
    const key = normalizeTaskName(t.name);
    if (!key) {
      hard.push({ rule: 'blank_name', ...line(t), message: `line ${t.display_order} has no name` });
      continue;
    }
    if (seen.has(key)) {
      const first = seen.get(key);
      hard.push({
        rule: 'repeated_name',
        ...line(t),
        other_display_order: first.display_order,
        message: `"${t.name}" (line ${t.display_order}) repeats "${first.name}" (line ${first.display_order}); a repeated name is a second task in Workamajig`,
      });
    } else seen.set(key, t);
  }

  const active = context.activeServices ?? null;
  for (const t of tasks) {
    const codes = new Set();
    for (const a of t.assignments ?? []) {
      if (!a.service_code) {
        hard.push({
          rule: 'blank_service',
          ...line(t),
          message: `a line on "${t.name}" names no service`,
        });
        continue;
      }
      if (codes.has(a.service_code)) {
        hard.push({
          rule: 'repeated_service',
          ...line(t),
          service_code: a.service_code,
          message: `"${t.name}" books ${a.service_code} twice; a template line is one service per task`,
        });
      }
      codes.add(a.service_code);
      if (active && !active.has(a.service_code)) {
        hard.push({
          rule: 'retired_service',
          ...line(t),
          service_code: a.service_code,
          message: `"${t.name}" books ${a.service_code}, which is not a live service`,
        });
      }
      if (a.hours < 0) {
        hard.push({
          rule: 'negative_hours',
          ...line(t),
          message: `"${t.name}" has a negative line`,
        });
      }
    }
    if (t.is_group && (t.assignments ?? []).length) {
      // A Workamajig summary task takes its hours from its children (8.2); hours on the group
      // itself would be written twice.
      hard.push({
        rule: 'group_with_assignments',
        ...line(t),
        message: `"${t.name}" is a group and carries its own assignments; a group's hours are its children's`,
      });
    }
    if (
      t.ends_before_delivery !== null &&
      t.ends_before_delivery !== undefined &&
      t.ends_before_delivery < 0
    ) {
      hard.push({
        rule: 'bad_timing',
        ...line(t),
        message: `"${t.name}" ends after the delivery date`,
      });
    }
    if (
      !t.is_group &&
      t.duration_weekdays !== null &&
      t.duration_weekdays !== undefined &&
      t.duration_weekdays < 1
    ) {
      hard.push({
        rule: 'bad_timing',
        ...line(t),
        message: `"${t.name}" lasts less than a working day`,
      });
    }
  }

  // Soft: what the designer shows and leaves to judgment.
  for (const t of tasks) {
    if (t.is_group && !tasks.some((c) => c.parent_order === t.display_order)) {
      soft.push({
        rule: 'empty_group',
        ...line(t),
        message: `"${t.name}" is a group with nothing in it`,
      });
    }
    if (!t.is_group && !(t.assignments ?? []).length) {
      soft.push({
        rule: 'no_assignments',
        ...line(t),
        message: `"${t.name}" books no hours to any service`,
      });
    }
    if (!t.is_group && context.history && context.referenceProjects) {
      const entry = context.history.get(normalizeTaskName(t.name));
      const refs = entry?.projects ?? 0;
      const max = (context.deadTaskRefMax ?? 0.05) * context.referenceProjects;
      if (refs <= max) {
        soft.push({
          rule: 'rarely_used',
          ...line(t),
          refs,
          message: `"${t.name}" is used by ${refs} of ${context.referenceProjects} reference schedules`,
        });
      }
    }
  }
  const total = round2(tasks.flatMap((t) => t.assignments ?? []).reduce((s, a) => s + a.hours, 0));
  if (context.rungMedianHours > 0 && total > 0) {
    const ratio = total / context.rungMedianHours;
    if (ratio > 2 || ratio < 0.5) {
      soft.push({
        rule: 'total_far_from_history',
        total_hours: total,
        median_hours: context.rungMedianHours,
        message: `the template totals ${total} h; the account's projects of this size have a median of ${round2(context.rungMedianHours)} h`,
      });
    }
  }
  return { hard, soft };
}

/**
 * What changed between two outlines, line by line. Matched by name (normalized) because that is
 * what Workamajig matches on and what a reader recognises; a renamed task therefore reads as a
 * removal and an addition unless it kept its `id`, which a draft copied from a version does.
 *
 * @param {object[]} before
 * @param {object[]} after
 */
export function diffOutlines(before, after) {
  const keyOf = (t) => t.source_id ?? t.copied_from_task_id ?? `name:${normalizeTaskName(t.name)}`;
  const a = new Map(before.map((t) => [keyOf(t), t]));
  const b = new Map(after.map((t) => [keyOf(t), t]));
  const added = [];
  const removed = [];
  const renamed = [];
  const rehoured = [];
  const moved = [];
  for (const [k, t] of b) {
    const o = a.get(k);
    if (!o) {
      added.push({ display_order: t.display_order, name: t.name, hours: t.hours });
      continue;
    }
    if (o.name !== t.name)
      renamed.push({ display_order: t.display_order, from: o.name, to: t.name });
    if (o.parent_order !== t.parent_order || o.display_order !== t.display_order) {
      moved.push({ name: t.name, from: o.display_order, to: t.display_order });
    }
    const lo = new Map((o.assignments ?? []).map((x) => [x.service_code, x.hours]));
    const ln = new Map((t.assignments ?? []).map((x) => [x.service_code, x.hours]));
    for (const code of new Set([...lo.keys(), ...ln.keys()])) {
      const from = lo.get(code) ?? null;
      const to = ln.get(code) ?? null;
      if (from !== to)
        rehoured.push({
          display_order: t.display_order,
          name: t.name,
          service_code: code,
          from,
          to,
        });
    }
  }
  for (const [k, o] of a) {
    if (!b.has(k)) removed.push({ display_order: o.display_order, name: o.name, hours: o.hours });
  }
  const total = (ts) =>
    round2(ts.flatMap((t) => t.assignments ?? []).reduce((s, x) => s + num(x.hours), 0));
  return {
    added,
    removed,
    renamed,
    rehoured,
    moved,
    hours_before: total(before),
    hours_after: total(after),
    unchanged:
      added.length + removed.length + renamed.length + rehoured.length + moved.length === 0,
  };
}
